import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { registerAdminStepUpHandler } from '../services/adminStepUp';

export default function AdminStepUpDialog() {
  const [challenge, setChallenge] = useState(null);
  const [credential, setCredential] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pendingRef = useRef(null);

  useEffect(() => registerAdminStepUpHandler((nextChallenge) => new Promise((resolve, reject) => {
    if (pendingRef.current) {
      const error = new Error('Another admin confirmation is already open');
      error.code = 'ADMIN_STEP_UP_BUSY';
      reject(error);
      return;
    }
    pendingRef.current = { resolve, reject };
    setCredential('');
    setError('');
    setChallenge(nextChallenge);
  })), []);

  const restoreFocus = () => {
    const priorFocus = challenge?.priorFocus;
    if (priorFocus && typeof priorFocus.focus === 'function' && document.contains(priorFocus)) {
      window.setTimeout(() => priorFocus.focus(), 0);
    }
  };

  const close = () => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setChallenge(null);
    setCredential('');
    setError('');
    setBusy(false);
    restoreFocus();
    if (!pending) return;
    const cancelError = new Error('Admin confirmation cancelled');
    cancelError.code = 'ADMIN_STEP_UP_CANCELLED';
    pending.reject(cancelError);
  };

  const confirm = async () => {
    if (!challenge?.verify || !credential.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      const response = await challenge.verify({ mode: challenge.mode, credential });
      const pending = pendingRef.current;
      pendingRef.current = null;
      setChallenge(null);
      setCredential('');
      setBusy(false);
      restoreFocus();
      pending?.resolve(response);
    } catch (requestError) {
      const code = requestError?.response?.data?.code;
      if (code === 'ADMIN_STEP_UP_INVALID' || code === 'ADMIN_STEP_UP_REQUIRED') {
        setError(requestError.response?.data?.error || 'That credential was not accepted. Try again.');
        setCredential('');
        setBusy(false);
        return;
      }
      const pending = pendingRef.current;
      pendingRef.current = null;
      setChallenge(null);
      setCredential('');
      setBusy(false);
      restoreFocus();
      pending?.reject(requestError);
    }
  };

  const isMfa = challenge?.mode === 'mfa';
  return (
    <Dialog
      open={Boolean(challenge)}
      onClose={() => { if (!busy) close(); }}
      maxWidth="xs"
      fullWidth
      aria-labelledby="admin-step-up-title"
      aria-describedby="admin-step-up-description"
    >
      <DialogTitle id="admin-step-up-title">Verify your identity</DialogTitle>
      <DialogContent>
        <Typography id="admin-step-up-description" sx={{ color: 'var(--text-secondary)', fontSize: '0.9rem', mb: 2 }}>
          {isMfa
            ? `Enter your authenticator code to ${challenge?.action || 'continue'}.`
            : `Enter your current Elite Edge password to ${challenge?.action || 'continue'}.`}
        </Typography>
        {error && (
          <Typography role="alert" aria-live="assertive" sx={{ color: 'error.main', fontSize: '0.85rem', mb: 1 }}>
            {error}
          </Typography>
        )}
        <TextField
          autoFocus
          fullWidth
          label={isMfa ? 'Authenticator code' : 'Current password'}
          type={isMfa ? 'text' : 'password'}
          value={credential}
          onChange={(event) => setCredential(event.target.value)}
          disabled={busy}
          error={Boolean(error)}
          autoComplete={isMfa ? 'one-time-code' : 'current-password'}
          inputProps={isMfa ? { inputMode: 'numeric', maxLength: 6 } : undefined}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && credential.trim()) confirm();
          }}
        />
      </DialogContent>
      <DialogActions sx={{ paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))' }}>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={confirm} disabled={!credential.trim() || busy}>
          {busy ? 'Checking...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
